/**
 * estate-config.ts — constants for the Estate entity dialog.
 *
 * Scope: state/county dropdowns, Letters-variant enum, CIP default candidates,
 * and helper-text strings explaining Letters Testamentary vs Letters of
 * Administration. Per Bill's 2026-04-23 admin-configurable architecture mandate
 * these will migrate to SQL catalog tables in a subsequent pass:
 *   - ProbateStates / ProbateCounties (admin-editable lookup)
 *   - LettersVariantCatalog (admin-editable enum + helper text)
 *
 * For v0.1.37 these are hardcoded here; Phase 2 moves them behind
 * `/v3/config/*` bundles read via `useConfig()`.
 *
 * Companion card: docs/port-design/category-cards/estate.md
 */

// -----------------------------------------------------------------------------
// Letters variants — drives the primary-doc slot + IdVerifyCode (Field 254)
// -----------------------------------------------------------------------------

export type EstateLettersVariant = 'Executor' | 'Administrator' | 'Affidavit';

export interface EstateLettersVariantOption {
  readonly value: EstateLettersVariant;
  readonly label: string;
  readonly docCode: string; // primary appointment doc
  readonly helperText: string;
}

export const ESTATE_LETTERS_VARIANTS: ReadonlyArray<EstateLettersVariantOption> = [
  {
    value: 'Executor',
    label: 'Executor — Letters Testamentary',
    docCode: 'LETTERS_TESTAMENTARY',
    helperText:
      'Use when the decedent left a valid will and the court has appointed an Executor named in the will. Upload the Letters Testamentary issued by the probate court.',
  },
  {
    value: 'Administrator',
    label: 'Court-appointed — Letters of Administration',
    docCode: 'LETTERS_ADMINISTRATION',
    helperText:
      'Use when the decedent died intestate (no will) and the court has appointed an Administrator. Upload the Letters of Administration issued by the probate court.',
  },
  {
    value: 'Affidavit',
    label: 'Small-estate affidavit (no formal probate case)',
    docCode: 'SMALL_ESTATE_AFFIDAVIT',
    helperText:
      'Use when the estate qualifies for Missouri small-estate affidavit (no formal probate case opened). Case number is blank; IdVerifyCode defaults to 8 (Other) per Bill\'s §13d D default.',
  },
];

// -----------------------------------------------------------------------------
// IdVerifyCode (Field 254) defaults — per card §13d
// -----------------------------------------------------------------------------

/**
 * CIP Field 254 default per Letters variant.
 *   - Executor / Administrator: case exists → 6 (State Documents, state-issued court filing)
 *   - Affidavit: no case → 8 (Other)
 *
 * TODO(bill): confirm §13d C (case-exists branch) — candidate 6 or 8. Default 6 here.
 * TODO(bill): confirm §13d D (no-case branch) — default 8 here per card.
 */
export const ESTATE_ID_VERIFY_CODE_BY_VARIANT: Record<EstateLettersVariant, string> = {
  Executor: '6',
  Administrator: '6',
  Affidavit: '8',
};

// -----------------------------------------------------------------------------
// US states (50 + DC + 5 US territories) — case filing state
// -----------------------------------------------------------------------------

export interface StateOption {
  readonly code: string;
  readonly name: string;
}

export const US_STATES: ReadonlyArray<StateOption> = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' }, { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' },
  { code: 'FL', name: 'Florida' }, { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' }, { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' }, { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' }, { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' }, { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' }, { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' }, { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' }, { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' }, { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' }, { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' }, { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' }, { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' }, { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' }, { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' },
  { code: 'DC', name: 'District of Columbia' },
  { code: 'AS', name: 'American Samoa' }, { code: 'GU', name: 'Guam' },
  { code: 'MP', name: 'Northern Mariana Islands' },
  { code: 'PR', name: 'Puerto Rico' }, { code: 'VI', name: 'US Virgin Islands' },
];

// -----------------------------------------------------------------------------
// Missouri counties — seeded datalist for the most common probate venue
// -----------------------------------------------------------------------------
//
// Officer can type any other county freeform — the datalist is a typing aid,
// not an enum. Missouri = PCB's home state, so the list is seeded. For other
// states, the field stays freeform (no datalist match) until the
// ProbateCounties catalog ships.
//

export const MISSOURI_COUNTIES: ReadonlyArray<string> = [
  'Adair', 'Andrew', 'Atchison', 'Audrain', 'Barry', 'Barton',
  'Bates', 'Benton', 'Bollinger', 'Boone', 'Buchanan', 'Butler',
  'Caldwell', 'Callaway', 'Camden', 'Cape Girardeau', 'Carroll',
  'Carter', 'Cass', 'Cedar', 'Chariton', 'Christian', 'Clark',
  'Clay', 'Clinton', 'Cole', 'Cooper', 'Crawford', 'Dade',
  'Dallas', 'Daviess', 'DeKalb', 'Dent', 'Douglas', 'Dunklin',
  'Franklin', 'Gasconade', 'Gentry', 'Greene', 'Grundy', 'Harrison',
  'Henry', 'Hickory', 'Holt', 'Howard', 'Howell', 'Iron',
  'Jackson', 'Jasper', 'Jefferson', 'Johnson', 'Knox', 'Laclede',
  'Lafayette', 'Lawrence', 'Lewis', 'Lincoln', 'Linn', 'Livingston',
  'Macon', 'Madison', 'Maries', 'Marion', 'McDonald', 'Mercer',
  'Miller', 'Mississippi', 'Moniteau', 'Monroe', 'Montgomery', 'Morgan',
  'New Madrid', 'Newton', 'Nodaway', 'Oregon', 'Osage', 'Ozark',
  'Pemiscot', 'Perry', 'Pettis', 'Phelps', 'Pike', 'Platte',
  'Polk', 'Pulaski', 'Putnam', 'Ralls', 'Randolph', 'Ray',
  'Reynolds', 'Ripley', 'Saline', 'Schuyler', 'Scotland', 'Scott',
  'Shannon', 'Shelby', 'St. Charles', 'St. Clair', 'St. Francois',
  'St. Louis', 'Ste. Genevieve', 'Stoddard', 'Stone', 'Sullivan',
  'Taney', 'Texas', 'Vernon', 'Warren', 'Washington', 'Wayne',
  'Webster', 'Worth', 'Wright',
  'St. Louis City', // independent city
];

// -----------------------------------------------------------------------------
// Default branch state — used to pre-fill Field 256 when new estate created
// -----------------------------------------------------------------------------

export const ESTATE_DEFAULT_STATE_CODE = 'MO';

// -----------------------------------------------------------------------------
// Helper-text strings for Letters variant disambiguation
// -----------------------------------------------------------------------------

export const ESTATE_HELPER_TEXT = {
  estateName:
    'Typically "Estate of [decedent full name]". Populates Core Director Field 6 (firstName) + Field 7 (lastName).',
  decedentName:
    'Full legal name of the deceased. Drives the Estate Name ("Estate of ...") display and links to the death certificate.',
  dateOfDeath:
    'Decedent\'s date of death. Stored in Field 30 (DOB) per §13d clarification — the estate itself has no birth date, so DOB is repurposed.',
  caseNumber:
    'Probate court case number (e.g., Missouri Case.Net docket). Populates Field 255. Leave blank for small-estate affidavit.',
  caseState:
    'State where probate was filed. Populates Field 256. Defaults to MO.',
  caseCounty:
    'County where probate was filed (e.g., Phelps, Pulaski). Appended to Field 256 ("MO · Phelps") for display.',
  executor:
    'Personal Representative (Executor if named in will, Administrator if court-appointed). This is the human signer on the estate; they have their own Individual CIF with its own CIP.',
  estateEin:
    'EIN issued by the IRS for the estate. TinCode = E. Required.',
  filingDate:
    'Date the court issued the Letters. Populates Field 257.',
  closureDate:
    'Estate closure date (optional). Populates Field 258 when known; otherwise blank (letters don\'t expire).',
  lettersTestamentaryVsAdministration:
    'Letters Testamentary = Executor named in will; Letters of Administration = court-appointed when no will. Upload exactly one based on variant above; seed SQL notes state these are OR-alternatives.',
} as const;
